import { useMemo } from "react";
import { MarketingTag, MarketingTagType } from "@/bryntum-lib/models/MarketingTag";
import { ProblemSolutionTagMap } from "@/bryntum-lib/models/ProblemSolutionTagMap";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";

export interface ManifestTagView {
  label: string;
  type: string;
  tag_group: string;
  description: string;
}

export interface ManifestTagMappingView {
  problem_label: string;
  solution_label: string;
}

const REVIEW_TAG_TYPES = new Set<string>([
  MarketingTagType.PROBLEM,
  MarketingTagType.SOLUTION,
  MarketingTagType.META,
]);

export const useManifestMarketingTags = () => {
  const { project, selectedBoard } = useSwivuBryntumContext();
  const boardId = selectedBoard?.record?.id as number | undefined;

  const { version: tagVersion } = useSwivuStore<MarketingTag>({ storeId: STORE_KEYS.marketingTagStore });
  const { version: mapVersion } = useSwivuStore<ProblemSolutionTagMap>({
    storeId: STORE_KEYS.problemSolutionTagMapStore,
  });

  return useMemo(() => {
    if (!project || boardId == null) {
      return { tags: [] as ManifestTagView[], mappings: [] as ManifestTagMappingView[], tagCount: 0 };
    }

    const allTags = (project.marketingTagStore?.query(() => true, true) ?? []) as MarketingTag[];
    const boardTags = allTags.filter((t) => t.board === boardId && REVIEW_TAG_TYPES.has(t.type));

    const tags: ManifestTagView[] = boardTags.map((t) => ({
      label: t.label,
      type: t.type,
      tag_group: t.tagGroup ?? t.type,
      description: t.description ?? "",
    }));

    const idToLabel = new Map<number, string>();
    for (const t of allTags) {
      if (t.board === boardId) idToLabel.set(t.id, t.label);
    }

    const allMaps = (project.problemSolutionTagMapStore?.query(() => true, true) ??
      []) as ProblemSolutionTagMap[];
    const mappings: ManifestTagMappingView[] = allMaps
      .filter((m) => m.board === boardId)
      .map((m) => {
        const problem_label = idToLabel.get(m.problemTag);
        const solution_label = idToLabel.get(m.solutionTag);
        if (!problem_label || !solution_label) return null;
        return { problem_label, solution_label };
      })
      .filter((m): m is ManifestTagMappingView => m != null);

    return { tags, mappings, tagCount: tags.length };
  }, [project, boardId, tagVersion, mapVersion]);
};
